import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ShieldCheck, CheckCircle, XCircle, ListChecks } from "@phosphor-icons/react";
import { LCShieldLogo } from "@/components/LCShieldLogo";
import { Seo } from "@/components/Seo";
import { FooterLinks } from "@/components/FooterLinks";

const H2 = ({ children }) => <h2 className="font-serif text-2xl text-white">{children}</h2>;
const P = ({ children }) => <p className="text-sm leading-relaxed text-lc-fog">{children}</p>;
const Item = ({ tone = "ok", children }) => (
  <li className="flex gap-2.5 text-sm leading-relaxed text-lc-fog">
    {tone === "ok"
      ? <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-lc-ok" />
      : <XCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-lc-critical" />}
    <span>{children}</span>
  </li>
);

export default function Guarantee() {
  const nav = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-lc-navy text-white font-sans antialiased flex flex-col" data-testid="guarantee-page">
      <Seo path="/guarantee"
        title="Audit Fee Guarantee | LC Shield"
        description="LC Shield's Audit Fee Guarantee: if we miss a discrepancy your bank cites in a refusal notice, and you send us that notice within 30 days of the refusal, we refund the audit fee you paid for that presentation." />

      <nav className="sticky top-0 z-40 border-b border-white/10 bg-lc-navy/85 backdrop-blur-md">
        <div className="mx-auto max-w-4xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="guarantee-logo">
            <LCShieldLogo className="text-lc-sky" size={24} />
            <span className="font-serif text-xl tracking-tight">LC Shield</span>
          </Link>
          <button onClick={() => nav("/workspace")} data-testid="guarantee-open-workspace"
            className="inline-flex items-center gap-2 rounded-sm bg-lc-sky px-4 py-2 text-sm font-semibold text-lc-navy hover:bg-white transition-colors duration-200">
            Open Workspace <ArrowRight weight="bold" size={15} />
          </button>
        </div>
      </nav>

      <header className="border-b border-white/10">
        <div className="mx-auto max-w-4xl px-6 py-14">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-lc-fog hover:text-white transition-colors duration-200">
            <ArrowLeft size={13} weight="bold" /> Back to home
          </Link>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="mt-6 flex items-center gap-3">
              <ShieldCheck size={32} weight="duotone" className="text-lc-ok shrink-0" />
              <h1 className="font-serif font-semibold tracking-tight text-4xl sm:text-5xl">LC Shield Audit Fee Guarantee</h1>
            </div>
            <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-lc-fog">Effective October 3, 2026</p>
          </motion.div>
        </div>
      </header>

      <main className="flex-1">
        <motion.article
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="mx-auto max-w-4xl px-6 py-14 space-y-10">

          <div className="rounded-md border border-lc-ok/30 bg-lc-ok/5 p-6">
            <p className="text-base leading-relaxed text-white">
              If LC Shield <strong className="text-lc-ok">misses a discrepancy that your bank cites in a refusal notice</strong>, and you send us that notice within <strong className="text-lc-ok">30 days</strong> of the refusal, we refund the audit fee you paid for that presentation.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-lc-fog">
              Free audits and sample reports have no audit fee and are not covered by the refund.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-lc-fog">
              <strong className="text-white">Audit fee defined:</strong> for Single Pass, the amount paid for that audit; for Pro and Firm, the plan price divided by the plan's included audits (USD 6.63 on Pro, USD 5.99 on Firm); Lifetime users who bring their own key pay no audit fee to LC Shield and are not eligible for a refund.
            </p>
            <p className="mt-3 flex flex-wrap items-center gap-x-2 font-mono text-[11px] uppercase tracking-wider text-lc-ok/80">
              <CheckCircle weight="bold" size={13} /> No long-term commitments. No data lock-in.
            </p>
          </div>

          <section className="space-y-4">
            <H2>What the guarantee covers</H2>
            <P>The guarantee applies to the audit fee you paid for a specific Letter of Credit presentation set that you audited with LC Shield before submitting it to your bank. It covers:</P>
            <ul className="space-y-2">
              <Item>A discrepancy that LC Shield failed to flag, but which falls squarely within a documentary rule under <strong className="text-white">UCP 600</strong> or standard banking practice under <strong className="text-white">ISBP 821 (2023)</strong>.</Item>
              <Item>Where the bank cites that missed discrepancy in a refusal notice for the same presentation.</Item>
              <Item>Refund of the <strong className="text-white">audit fee you paid</strong> for that presentation.</Item>
            </ul>
          </section>

          <section className="space-y-4">
            <H2><span className="inline-flex items-center gap-2"><ListChecks size={20} weight="duotone" className="text-lc-sky" /> Eligibility requirements</span></H2>
            <P>To qualify for a refund under this guarantee, all of the following must be true:</P>
            <ul className="space-y-2">
              <Item>The presentation set was audited in LC Shield and returned a result (score + discrepancy report) before you presented to the bank.</Item>
              <Item>You presented the documents substantially as audited — the missed discrepancy existed in the exact documents scanned, not in later edits or replacements.</Item>
              <Item>You submit the bank's written discrepancy advice (SWIFT MT734 / MT750 or the bank's refusal notice) within <strong className="text-white">30 days</strong> of the refusal.</Item>
              <Item>The discrepancy is documentary in nature and determinable on the face of the documents under UCP 600 / ISBP 821 (2023).</Item>
            </ul>
          </section>

          <section className="space-y-4">
            <H2>What is not covered</H2>
            <ul className="space-y-2">
              <Item tone="bad">Non-documentary conditions that UCP 600 Article 14(h) requires banks to disregard.</Item>
              <Item tone="bad">Commercial disputes, fraud, sanctions, force majeure, or a buyer simply refusing to pay for non-documentary reasons.</Item>
              <Item tone="bad">Discrepancies introduced after the audit, or documents changed, re-issued or dated differently from what was scanned.</Item>
              <Item tone="bad">Late shipment or expiry breaches that were correctly flagged by LC Shield and presented anyway.</Item>
              <Item tone="bad">Bank charges other than discrepancy fees (e.g. handling, courier, amendment or confirmation charges).</Item>
            </ul>
          </section>

          <section className="space-y-4">
            <H2>How to claim</H2>
            <P>Email <a href="mailto:support@getlcshield.com" className="text-lc-sky hover:underline">support@getlcshield.com</a> with your audit reference (shown on the report), the bank's discrepancy advice, and the presentation set you audited. We review each claim within 5 business days and, if eligible, refund the audit fee to your original payment method.</P>
            <P className="text-lc-steel">This guarantee is a goodwill commercial assurance from LC Shield (DZent Limited) and does not constitute legal, banking or professional advice. LC Shield is an automated pre-audit assistant and does not replace your bank's own document examination. Refunds are limited to the audit fee paid for the affected set.</P>
          </section>

          <div className="rounded-sm border border-lc-sky/30 bg-gradient-to-br from-lc-slate/60 to-lc-navy p-8">
            <h3 className="font-serif text-2xl text-white">Audit with confidence</h3>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-lc-fog">
              Run your presentation through LC Shield before the bank does — every finding cites its UCP 600 / ISBP 821 (2023) article. Paid audits are backed by our Audit Fee Guarantee.
            </p>
            <button onClick={() => nav("/workspace")} data-testid="guarantee-cta"
              className="group mt-6 inline-flex items-center gap-2 rounded-sm bg-lc-sky px-6 py-3.5 text-sm font-semibold text-lc-navy hover:bg-white transition-colors duration-200">
              Try a free audit
              <ArrowRight weight="bold" size={15} className="group-hover:translate-x-0.5 transition-transform duration-200" />
            </button>
          </div>
        </motion.article>
      </main>

      <footer className="border-t border-white/10">
        <div className="mx-auto max-w-4xl px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <LCShieldLogo className="text-lc-sky" size={20} />
            <span className="font-serif text-lg text-white">LC Shield</span>
          </div>
          <FooterLinks variant="dark" />
        </div>
      </footer>
    </div>
  );
}
